import { RouteConfig } from 'vue-router'
import i18n from '@/plugins/i18n'

export const RouteNames = {
  LOGIN: 'login',
  LOGOUT: 'logout',
  CREATE_PASSWORD: 'createPassword',
  OVERVIEW: 'overview',
  MEDIA_LIBRARY: 'mediaLibrary',
  DASHBOARD_PLAYERS: 'dashboardPlayers',
  LINEUPS: 'lineups',
  LOAD: 'load',
  PERFORMANCE_EVENT: 'performanceEvent',
  PERFORMANCE_EVENT_PLAYER: 'performanceEventPlayer',
  PERFORMANCE_ADVANCED: 'performanceAdvanced',
  PERFORMANCE_WEEKLY_LOAD: 'performanceWeeklyLoad',
  PERFORMANCE_PERIOD_SUMMARY: 'performancePeriodSummary',
  DIAGNOSTICS: 'diagnostics',
  DIAGNOSTIC_PROTOCOLS: 'diagnosticProtocols',
  DIAGNOSTIC_COMPARISON: 'diagnosticComparison',
  MEDICAL: 'medical',
  WELLNESS: 'wellness',
  COMPETITIONS: 'competitions',
  PLAYER_DETAILS: 'playerDetails',
  COACH_DETAILS: 'coachDetails',
  PLAYER_OVERVIEW: 'playerOverview',
  PLAYER_LOAD: 'playerLoad',
  PLAYER_TRAINING_EXERCISES: 'playerTrainingExercises',
  PLAYER_SMART_ASSISTANT: 'playerSmartAssistant',
  PLAYER_DIAGNOSTICS: 'playerDiagnostics',
  PLAYER_MEDICAL: 'playerMedical',
  PLAYER_MEDIA: 'playerMedia',
  PLAYER_NOTES: 'playerNotes',
  PLAYER_ATTENDANCE: 'playerAttendance',
  PLAYER_WELLNESS: 'playerWellness',
  PLAYER_EVENTS: 'playerEvents',
  PLAYER_DAILY_ACTIVITY: 'playerDailyActivity',
  PLAYER_SCOUTING: 'playerScouting',
  PLAYER_SESSIONS: 'playerSessions',
  PLAYER_NUTRITION: 'playerNutrition',
  NUTRITION: 'nutrition',
  EVENTS: 'events',
  ADMIN_PLAYERS: 'adminPlayers',
  ADMIN_TEAMS: 'adminTeams',
  ADMIN_STAFF: 'adminStaff',
  ADMIN_LOCATIONS: 'adminLocations',
  ADMIN_PERIOD_TAGS: 'adminPeriodTags',
  ADMIN_MATCH_OPPONENTS: 'adminMatchOpponents',
  ADMIN_PLAYER_FORM: 'adminPlayerForm',
  ADMIN_STAFF_FORM: 'adminStaffForm',
  ADMIN_PLAYER_EDIT: 'adminPlayerEdit',
  ADMIN_STAFF_EDIT: 'adminStaffEdit',
  ADMIN_DEVICES: 'adminDevices',
  ADMIN_MEMBERSHIP_PLANS: 'adminMembershipPlans',
  ADMIN_PARENTS: 'adminParents',
  ADMIN_POLAR_TEAM: 'adminPolarTeam',
  ADMIN_GPEXE: 'adminGpexe',
  ADMIN_FIELDWIZ: 'adminFieldwiz',
  ADMIN_CATAPULT: 'adminCatapult',
  ADMIN_VALD: 'adminVald',
  ADMIN_KINEXON: 'adminKinexon',
  ADMIN_COMET: 'adminComet',
  ADMIN_STATSPORTS: 'adminStatsports',
  ADMIN_WIMU: 'adminWimu',
  ADMIN_LIBRE: 'adminLibre',
  ADMIN_QUESTIONNAIRES: 'adminQuestionnaires',
  FORGOT_PASSWORD: 'forgotPassword',
  FINISH_SIGNUP: 'finishSignup',
  MEMBERSHIP_FEES: 'membershipFees',
  TRAINING_PROTOCOLS: 'trainingProtocols',
  TRAINING_EXERCISES: 'trainingExercises',
  SESSIONS_OVERVIEW: 'sessionsOverview',
  INSIDERS_HUB: 'insidersHub',
  DRILL_BUILDER: 'drillBuilder',
  TRAINING_PROGRAMS: 'trainingPrograms',
  TRAINING_PROGRAM_INSTANCES: 'trainingProgramInstances',
  SIGNUP: 'signup',
  ADMIN_BILLING: 'adminBilling',
  DAILY_ACTIVITY: 'dailyActivity',
  REPORTS: 'reports',
  EVENT_STATS: 'eventStats',
  SCOUTING: 'scouting',
  COACH_INSIGHTS: 'coachInsights',
  COACH_INSIGHTS_INDIVIDUAL: 'coachInsightsIndividual',
  PLAYER_INSIGHTS: 'playerInsights',
  PLAYER_INSIGHTS_INDIVIDUAL: 'playerInsightsIndividual'
}

declare module 'vue/types/vue' {
  interface Vue {
    $routeNames: typeof RouteNames;
  }
}

const routes: RouteConfig[] = [
  {
    path: '/',
    redirect: '/overview'
  },
  {
    path: '/login',
    name: RouteNames.LOGIN,
    component: () => import('@/views/LoginView.vue')
  },
  {
    path: '/logout',
    name: RouteNames.LOGOUT,
    component: () => import('@/views/LogoutView.vue')
  },
  {
    path: '/signup',
    name: RouteNames.SIGNUP,
    component: () => import('@/views/SignupView.vue')
  },
  {
    path: '/signup/finish',
    name: RouteNames.FINISH_SIGNUP,
    component: () => import('@/views/ForgotPasswordView.vue')
  },
  {
    path: '/reset-password',
    alias: '/forgot-password',
    name: RouteNames.FORGOT_PASSWORD,
    component: () => import('@/views/ForgotPasswordView.vue')
  },
  {
    path: '/players/:id',
    name: RouteNames.PLAYER_DETAILS,
    component: () => import('@/views/PlayerDetailsView.vue'),
    meta: {
      crumb: i18n.t('crumb.players')
    }
  },
  {
    path: '/coaches/:id',
    name: RouteNames.COACH_DETAILS,
    component: () => import('@/views/CoachDetailsView.vue')
  },
  {
    path: '/players/:id/overview',
    name: RouteNames.PLAYER_OVERVIEW,
    component: () => import('@/views/player/PlayerOverviewView.vue')
  },
  {
    path: '/players/:id/training-load',
    name: RouteNames.PLAYER_LOAD,
    component: () => import('@/components/players/PlayerLoadView.vue')
  },
  {
    path: '/players/:id/training-exercises',
    name: RouteNames.PLAYER_TRAINING_EXERCISES,
    component: () => import('@/views/player/PlayerTrainingsView.vue')
  },
  {
    path: '/players/:id/smart-assistant',
    name: RouteNames.PLAYER_SMART_ASSISTANT,
    component: () => import('@/views/player/PlayerSmartAssistantAlertsView.vue')
  },
  {
    path: '/players/:id/diagnostics',
    name: RouteNames.PLAYER_DIAGNOSTICS,
    component: () => import('@/views/player/PlayerDiagnosticView.vue')
  },
  {
    path: '/players/:id/medical',
    name: RouteNames.PLAYER_MEDICAL,
    component: () => import('@/views/player/PlayerMedicalView.vue')
  },
  {
    path: '/players/:id/media',
    name: RouteNames.PLAYER_MEDIA,
    component: () => import('@/views/player/PlayerMediaView.vue')
  },
  {
    path: '/players/:id/notes',
    name: RouteNames.PLAYER_NOTES,
    component: () => import('@/views/player/PlayerNotesView.vue')
  },
  {
    path: '/players/:id/attendance',
    name: RouteNames.PLAYER_ATTENDANCE,
    component: () => import('@/views/player/PlayerEventAttendanceView.vue')
  },
  {
    path: '/players/:id/wellness',
    name: RouteNames.PLAYER_WELLNESS,
    component: () => import('@/views/player/PlayerWellnessView.vue')
  },
  {
    path: '/players/:id/events',
    name: RouteNames.PLAYER_EVENTS,
    component: () => import('@/views/player/PlayerEventsView.vue')
  },
  {
    path: '/players/:id/daily-activity',
    name: RouteNames.PLAYER_DAILY_ACTIVITY,
    component: () => import('@/views/player/PlayerDailyActivityView.vue')
  },
  {
    path: '/players/:id/scouting',
    name: RouteNames.PLAYER_SCOUTING,
    component: () => import('@/views/player/PlayerScoutingView.vue')
  },
  {
    path: '/players/:id/sessions',
    name: RouteNames.PLAYER_SESSIONS,
    component: () => import('@/components/players/PlayerSessionsView.vue')
  },
  {
    path: '/players/:id/nutrition',
    name: RouteNames.PLAYER_NUTRITION,
    component: () => import('@/views/player/PlayerNutritionView.vue')
  },
  {
    path: '/overview',
    name: RouteNames.OVERVIEW,
    component: () => import('@/views/OverviewView.vue')
  },
  {
    path: '/players-list',
    name: RouteNames.DASHBOARD_PLAYERS,
    component: () => import('@/views/DashboardPlayersView.vue'),
    meta: {
      crumb: 'Players List'
    }
  },
  {
    path: '/lineups',
    name: RouteNames.LINEUPS,
    component: () => import('@/views/LineupsView.vue'),
    meta: {
      crumb: 'Lineups'
    }
  },
  {
    path: '/events',
    name: RouteNames.EVENTS,
    component: () => import('@/views/EventsView.vue'),
    meta: {
      crumb: i18n.t('crumb.events')
    }
  },
  {
    path: '/media-library',
    name: RouteNames.MEDIA_LIBRARY,
    component: () => import('@/views/MediaLibraryView.vue'),
    meta: {
      crumb: i18n.t('crumb.mediaLibrary')
    }
  },
  {
    path: '/event-stats',
    name: RouteNames.EVENT_STATS,
    component: () => import('@/views/EventStatsView.vue'),
    meta: {
      crumb: i18n.t('crumb.eventStats')
    }
  },
  {
    path: '/performance-event',
    name: RouteNames.PERFORMANCE_EVENT,
    component: () => import('@/views/PerformanceEventView.vue')
  },
  {
    path: '/performance-events/:eventId/players/:playerId',
    name: RouteNames.PERFORMANCE_EVENT_PLAYER,
    component: () => import('@/views/PerformanceEventPlayerView.vue')
  },
  {
    path: '/performance-advanced',
    name: RouteNames.PERFORMANCE_ADVANCED,
    component: () => import('@/views/PerformanceAdvancedView.vue')
  },
  {
    path: '/performance-weekly-load',
    name: RouteNames.PERFORMANCE_WEEKLY_LOAD,
    component: () => import('@/views/WeeklyLoadOverviewView.vue')
  },
  {
    path: '/scouting',
    name: RouteNames.SCOUTING,
    component: () => import('@/views/ScoutingView.vue')
  },
  {
    path: '/period-summary/:id',
    name: RouteNames.PERFORMANCE_PERIOD_SUMMARY,
    component: () => import('@/views/PerformancePeriodSummaryView.vue')
  },
  {
    path: '/diagnostics',
    name: RouteNames.DIAGNOSTICS,
    component: () => import('@/views/DiagnosticsView.vue'),
    meta: {
      crumb: i18n.t('crumb.diagnostics')
    }
  },
  {
    path: '/diagnostic-protocols',
    name: RouteNames.DIAGNOSTIC_PROTOCOLS,
    component: () => import('@/views/DiagnosticProtocolsView.vue')
  },
  {
    path: '/diagnostic-comparison',
    name: RouteNames.DIAGNOSTIC_COMPARISON,
    component: () => import('@/views/DiagnosticComparisonView.vue')
  },
  {
    path: '/medical',
    name: RouteNames.MEDICAL,
    component: () => import('@/views/MedicalView.vue'),
    meta: {
      crumb: i18n.t('crumb.medical')
    }
  },
  {
    path: '/wellness',
    name: RouteNames.WELLNESS,
    component: () => import('@/views/WellnessView.vue')
  },
  {
    path: '/nutrition',
    name: RouteNames.NUTRITION,
    component: () => import('@/views/NutritionView.vue')
  },
  {
    path: '/competitions',
    name: RouteNames.COMPETITIONS,
    component: () => import('@/views/CompetitionsView.vue')
  },
  {
    path: '/daily-activity',
    name: RouteNames.DAILY_ACTIVITY,
    component: () => import('@/views/DailyActivityView.vue')
  },
  {
    path: '/load',
    name: RouteNames.LOAD,
    component: () => import('@/views/LoadView.vue')
  },
  {
    path: '/membership',
    name: RouteNames.MEMBERSHIP_FEES,
    component: () => import('@/views/MembershipFeesView.vue'),
    meta: {
      crumb: i18n.t('crumb.membershipFees')
    }
  },
  {
    path: '/admin/players',
    name: RouteNames.ADMIN_PLAYERS,
    component: () => import('@/views/admin/AdminPlayersView.vue'),
    meta: {
      crumb: i18n.t('crumb.players')
    }
  },
  {
    path: '/admin/teams',
    name: RouteNames.ADMIN_TEAMS,
    component: () => import('@/views/admin/AdminTeamsView.vue'),
    meta: {
      crumb: i18n.t('crumb.teams')
    }
  },
  {
    path: '/admin/staff',
    name: RouteNames.ADMIN_STAFF,
    component: () => import('@/views/admin/AdminStaffView.vue'),
    meta: {
      crumb: i18n.t('crumb.coaches')
    }
  },
  {
    path: '/admin/locations',
    name: RouteNames.ADMIN_LOCATIONS,
    component: () => import('@/views/admin/AdminLocationsView.vue'),
    meta: {
      crumb: i18n.t('crumb.locations')
    }
  },
  {
    path: '/admin/period-tags',
    name: RouteNames.ADMIN_PERIOD_TAGS,
    component: () => import('@/views/admin/AdminPeriodTagsView.vue'),
    meta: {
      crumb: i18n.t('crumb.period_tags')
    }
  },
  {
    path: '/admin/match-opponents',
    name: RouteNames.ADMIN_MATCH_OPPONENTS,
    component: () => import('@/views/admin/AdminMatchOpponentsView.vue'),
    meta: {
      crumb: i18n.t('crumb.match_opponents')
    }
  },
  {
    path: '/admin/devices',
    name: RouteNames.ADMIN_DEVICES,
    component: () => import('@/views/admin/AdminDevicesView.vue'),
    meta: {
      crumb: i18n.t('crumb.devices')
    }
  },
  {
    path: '/admin/membership-plans',
    name: RouteNames.ADMIN_MEMBERSHIP_PLANS,
    component: () => import('@/views/admin/AdminMembershipPlansView.vue'),
    meta: {
      crumb: i18n.t('crumb.membershipPlans')
    }
  },
  {
    path: '/admin/parents',
    name: RouteNames.ADMIN_PARENTS,
    component: () => import('@/views/admin/AdminParentsView.vue'),
    meta: {
      crumb: i18n.t('crumb.parents')
    }
  },
  {
    path: '/admin/integrations/polar-team',
    name: RouteNames.ADMIN_POLAR_TEAM,
    component: () => import('@/views/admin/AdminPolarTeamView.vue'),
    meta: {
      crumb: i18n.t('crumb.polarTeam')
    }
  },
  {
    path: '/admin/integrations/gpexe',
    name: RouteNames.ADMIN_GPEXE,
    component: () => import('@/views/admin/AdminGpexeView.vue'),
    meta: {
      crumb: i18n.t('crumb.gpexe')
    }
  },
  {
    path: '/admin/integrations/insiders',
    name: RouteNames.ADMIN_FIELDWIZ,
    component: () => import('@/views/admin/AdminFieldwizView.vue'),
    meta: {
      crumb: i18n.t('crumb.fieldWiz')
    }
  },
  {
    path: '/admin/integrations/catapult',
    name: RouteNames.ADMIN_CATAPULT,
    component: () => import('@/views/admin/AdminCatapultView.vue'),
    meta: {
      crumb: i18n.t('crumb.catapult')
    }
  },
  {
    path: '/admin/vald',
    name: RouteNames.ADMIN_VALD,
    component: () => import('@/views/admin/AdminValdView.vue')
  },
  {
    path: '/admin/kinexon',
    name: RouteNames.ADMIN_KINEXON,
    component: () => import('@/views/admin/AdminKinexonView.vue')
  },
  {
    path: '/admin/comet',
    name: RouteNames.ADMIN_COMET,
    component: () => import('@/views/admin/AdminCometView.vue')
  },
  {
    path: '/admin/statsports',
    name: RouteNames.ADMIN_STATSPORTS,
    component: () => import('@/views/admin/AdminStatsportsView.vue')
  },
  {
    path: '/admin/wimu',
    name: RouteNames.ADMIN_WIMU,
    component: () => import('@/views/admin/AdminWimuView.vue')
  },
  {
    path: '/admin/libre',
    name: RouteNames.ADMIN_LIBRE,
    component: () => import('@/views/admin/AdminLibreView.vue')
  },
  {
    path: '/admin/players/add',
    name: RouteNames.ADMIN_PLAYER_FORM,
    component: () => import('@/views/admin/AdminPlayerForm.vue'),
    meta: {
      crumb: i18n.t('crumb.player')
    }
  },
  {
    path: '/admin/staff/add',
    name: RouteNames.ADMIN_STAFF_FORM,
    component: () => import('@/views/admin/AdminStaffForm.vue')
  },
  {
    path: '/admin/players/:id',
    name: RouteNames.ADMIN_PLAYER_EDIT,
    component: () => import('@/views/admin/AdminPlayerEditForm.vue'),
    meta: {
      crumb: i18n.t('crumb.editPlayer')
    }
  },
  {
    path: '/admin/staff/:id',
    name: RouteNames.ADMIN_STAFF_EDIT,
    component: () => import('@/views/admin/AdminStaffEditForm.vue')
  },
  {
    path: '/admin/billing',
    name: RouteNames.ADMIN_BILLING,
    component: () => import('@/views/admin/AdminBillingView.vue'),
    meta: {
      crumb: 'Billing'
    }
  },
  {
    path: '/admin/questionnaires',
    name: RouteNames.ADMIN_QUESTIONNAIRES,
    component: () => import('@/views/admin/AdminQuestionnairesView.vue'),
    meta: {
      crumb: 'Questionnaires'
    }
  },
  {
    path: '/training-protocols',
    name: RouteNames.TRAINING_PROTOCOLS,
    component: () => import('@/views/TrainingProtocolsView.vue'),
    meta: {
      crumb: i18n.t('crumb.trainingProtocols')
    }
  },
  {
    path: '/sessions-overview',
    name: RouteNames.SESSIONS_OVERVIEW,
    component: () => import('@/views/SessionsOverviewView.vue')
  },
  {
    path: '/training-exercises',
    name: RouteNames.TRAINING_EXERCISES,
    component: () => import('@/views/TrainingExercisesView.vue'),
    meta: {
      crumb: i18n.t('crumb.trainingExercises')
    }
  },
  {
    path: '/drill-builder',
    name: RouteNames.DRILL_BUILDER,
    component: () => import('@/views/DrillListView.vue'),
    meta: {
      crumb: 'UX Drill'
    }
  },
  {
    path: '/insiders-hub',
    name: RouteNames.INSIDERS_HUB,
    component: () => import('@/views/InsidersHubView.vue')
  },
  {
    path: '/training-programs',
    name: RouteNames.TRAINING_PROGRAMS,
    component: () => import('@/views/TrainingProgramsView.vue'),
    meta: {
      crumb: i18n.t('sidebar.training_programs.program_library.label')
    }
  },
  {
    path: '/training-program-instances',
    name: RouteNames.TRAINING_PROGRAM_INSTANCES,
    component: () => import('@/views/TrainingProgramInstancesView.vue'),
    meta: {
      crumb: i18n.t('sidebar.training_programs.active_programs.label')
    }
  },
  {
    path: '/coach-insights',
    name: RouteNames.COACH_INSIGHTS,
    component: () => import('@/views/CoachInsightsView.vue'),
    meta: {
      crumb: i18n.t('sidebar.training_protocols.coach_insights.label')
    }
  },
  {
    path: '/coach-insights/:coachId',
    name: RouteNames.COACH_INSIGHTS_INDIVIDUAL,
    component: () => import('@/views/IndividualExerciseInsightsView.vue'),
    meta: {
      crumb: i18n.t('individual_insights.view_name')
    }
  },
  {
    path: '/player-insights',
    name: RouteNames.PLAYER_INSIGHTS,
    component: () => import('@/views/PlayerInsightsView.vue'),
    meta: {
      crumb: i18n.t('sidebar.training_protocols.player_insights.label')
    }
  },
  {
    path: '/players/:id/exercise-insights',
    name: RouteNames.PLAYER_INSIGHTS_INDIVIDUAL,
    component: () => import('@/views/IndividualExerciseInsightsView.vue'),
    meta: {
      crumb: i18n.t('individual_insights.view_name')
    }
  },
  {
    path: '/reports',
    name: RouteNames.REPORTS,
    component: () => import('@/views/ReportsView.vue')
  }
]

export default routes
